import { RenderLayoutError } from "../errors";
import type { RenderContext } from "../types/render";
import type {
  CompiledContainer,
  LayoutAdjustmentDiagnostic,
  PositionedElement,
} from "../types/template";
import { roundMm } from "../utils/units";
import type { Rect } from "./anchorPlacement";
import { resolveAnchorOrigin } from "./anchorPlacement";
import {
  calculateCrossSize,
  calculateMainSize,
  measureElement,
} from "./measureElement";
import { assertFitsInside } from "./overflowChecks";

interface LayoutContainerResult {
  elements: PositionedElement[];
  diagnostics: LayoutAdjustmentDiagnostic[];
}

export function layoutContainer(
  container: CompiledContainer,
  drawable: Rect,
  valuesByElementId: Map<string, string>,
  hiddenElementIds: Set<string>,
  ctx: RenderContext,
): LayoutContainerResult {
  const outerWidth = container.widthMm ?? drawable.width;
  const innerWidth =
    outerWidth - container.paddingMm.left - container.paddingMm.right;

  if (innerWidth <= 0) {
    throw new RenderLayoutError(
      `Container '${container.id}' has no inner width.`,
    );
  }

  const provisionalCrossLimit =
    container.flow === "vertical"
      ? innerWidth
      : Math.max(0.001, container.heightMm ?? 8);

  const textScaleCaps = new Map<string, number>();
  const textMinScales = new Map<string, number>();
  const shrinkStep = 0.05;
  const visibleElements = container.elements.filter(
    (element) => !hiddenElementIds.has(element.id),
  );

  for (const element of visibleElements) {
    if (element.type !== "text" || element.font.overflowStrategy !== "shrink") {
      continue;
    }
    textScaleCaps.set(element.id, 1);
    textMinScales.set(
      element.id,
      Math.min(1, element.font.minHeightMm / element.font.heightMm),
    );
  }

  const measureState = () => {
    const measuredElements = visibleElements.map((element) =>
      measureElement(
        element,
        valuesByElementId.get(element.id),
        ctx,
        container.flow,
        provisionalCrossLimit,
        textScaleCaps.get(element.id) ?? 1,
      ),
    );

    const requiredMain =
      measuredElements.reduce(
        (sum, measured) => sum + calculateMainSize(container.flow, measured),
        0,
      ) +
      Math.max(0, measuredElements.length - 1) * container.gapMm;

    const requiredCross = measuredElements.reduce(
      (max, measured) =>
        Math.max(max, calculateCrossSize(container.flow, measured)),
      0,
    );

    const outerHeight =
      container.heightMm ??
      (container.flow === "vertical"
        ? requiredMain + container.paddingMm.top + container.paddingMm.bottom
        : requiredCross + container.paddingMm.top + container.paddingMm.bottom);

    const innerHeight =
      outerHeight - container.paddingMm.top - container.paddingMm.bottom;
    const availableMain =
      container.flow === "vertical" ? innerHeight : innerWidth;

    return {
      measuredElements,
      requiredMain,
      outerHeight,
      innerHeight,
      availableMain,
    };
  };

  let state = measureState();

  if (state.outerHeight <= 0 || state.innerHeight <= 0) {
    throw new RenderLayoutError(
      `Container '${container.id}' has invalid height.`,
    );
  }

  if (
    container.overflow === "error" &&
    state.requiredMain > state.availableMain + 0.001 &&
    textScaleCaps.size > 0
  ) {
    while (state.requiredMain > state.availableMain + 0.001) {
      let changed = false;

      for (const [elementId, scale] of textScaleCaps) {
        const minScale = textMinScales.get(elementId) ?? 1;
        const nextScale = Math.max(minScale, scale - shrinkStep);
        if (nextScale < scale) {
          textScaleCaps.set(elementId, nextScale);
          changed = true;
        }
      }

      if (!changed) {
        break;
      }

      state = measureState();
      if (state.outerHeight <= 0 || state.innerHeight <= 0) {
        throw new RenderLayoutError(
          `Container '${container.id}' has invalid height after shrink fitting.`,
        );
      }
    }
  }

  const measuredElements = state.measuredElements;
  const requiredMain = state.requiredMain;
  const outerHeight = state.outerHeight;
  const innerHeight = state.innerHeight;

  const anchorOrigin = resolveAnchorOrigin(
    container.anchor,
    drawable,
    outerWidth,
    outerHeight,
  );
  const containerRect: Rect = {
    x: anchorOrigin.x + container.xMm,
    y: anchorOrigin.y + container.yMm,
    width: outerWidth,
    height: outerHeight,
  };

  if (container.overflow === "error") {
    const containerOverflow =
      containerRect.x < drawable.x ||
      containerRect.y < drawable.y ||
      containerRect.x + containerRect.width > drawable.x + drawable.width ||
      containerRect.y + containerRect.height > drawable.y + drawable.height;

    if (containerOverflow) {
      throw new RenderLayoutError(
        `Container '${container.id}' overflows drawable canvas bounds.`,
      );
    }
  }

  const availableMain = state.availableMain;
  if (container.overflow === "error" && requiredMain > availableMain + 0.001) {
    throw new RenderLayoutError(
      `Container '${container.id}' content overflow. Required ${requiredMain}mm on ${container.flow} axis, available ${availableMain}mm.`,
    );
  }

  const justifyOffset =
    container.justify === "start"
      ? 0
      : container.justify === "center"
        ? Math.max(0, (availableMain - requiredMain) / 2)
        : Math.max(0, availableMain - requiredMain);

  let cursor = justifyOffset;
  const positionedElements: PositionedElement[] = [];
  const diagnostics: LayoutAdjustmentDiagnostic[] = [];

  const isVertical = container.flow === "vertical";
  const crossExtent = isVertical ? innerWidth : innerHeight;
  const crossPadStart = isVertical
    ? container.paddingMm.left
    : container.paddingMm.top;
  const mainPadStart = isVertical
    ? container.paddingMm.top
    : container.paddingMm.left;

  for (const measured of measuredElements) {
    if (measured.diagnostic !== undefined) {
      diagnostics.push(measured.diagnostic);
    }

    const marginMainStart = isVertical
      ? measured.margin.top
      : measured.margin.left;
    const marginMainEnd = isVertical
      ? measured.margin.bottom
      : measured.margin.right;
    const marginCrossStart = isVertical
      ? measured.margin.left
      : measured.margin.top;
    const marginCrossEnd = isVertical
      ? measured.margin.right
      : measured.margin.bottom;
    const measuredMainSize = isVertical ? measured.heightMm : measured.widthMm;
    const measuredCrossSize = isVertical ? measured.widthMm : measured.heightMm;
    const stretchDimUndefined = isVertical
      ? measured.element.widthMm === undefined
      : measured.element.heightMm === undefined;

    const crossSpace = crossExtent - marginCrossStart - marginCrossEnd;
    const canStretch = container.align === "stretch" && stretchDimUndefined;
    const resolvedCrossSize = canStretch
      ? Math.max(0.001, crossSpace)
      : measuredCrossSize;

    const crossOffset =
      container.align === "start" || container.align === "stretch"
        ? crossPadStart + marginCrossStart
        : container.align === "center"
          ? crossPadStart + (crossExtent - resolvedCrossSize) / 2
          : crossPadStart + crossExtent - resolvedCrossSize - marginCrossEnd;

    const mainPos =
      containerRect[isVertical ? "y" : "x"] +
      mainPadStart +
      cursor +
      marginMainStart;
    const crossPos = containerRect[isVertical ? "x" : "y"] + crossOffset;

    const xMm = roundMm(
      (isVertical ? crossPos : mainPos) + measured.element.nudgeXMm,
    );
    const yMm = roundMm(
      (isVertical ? mainPos : crossPos) + measured.element.nudgeYMm,
    );

    const positioned: PositionedElement = {
      containerId: container.id,
      element: measured.element,
      xMm,
      yMm,
      widthMm: roundMm(isVertical ? resolvedCrossSize : measuredMainSize),
      heightMm: roundMm(isVertical ? measuredMainSize : resolvedCrossSize),
      textValue:
        measured.textValue ?? valuesByElementId.get(measured.element.id),
      textLines: measured.textLines,
      lineCount: measured.lineCount,
      textFontOverride: measured.textFontOverride,
      textOverflowed: measured.textOverflowed,
      textEllipsized: measured.textEllipsized,
    };

    assertFitsInside(
      containerRect,
      positioned,
      container.overflow,
      `Container '${container.id}'`,
    );
    positionedElements.push(positioned);

    cursor +=
      marginMainStart + measuredMainSize + marginMainEnd + container.gapMm;
  }

  return {
    elements: positionedElements,
    diagnostics,
  };
}
